import React from "react";
import { makeStyles } from "@material-ui/core";
import WarningIcon from "@material-ui/icons/Warning";
import { colorsLight } from "../../themes";
import { Body2 } from "../Typography";

interface Props {
  env:
    | "local"
    | "dev"
    | "uat"
    | "intake"
    | "react-18"
    | "review"
    | "prod"
    | "demo"
    | "demo2"
    | "staging"
    | "preprod"
    | "unknown";
  children?: React.ReactNode;
  useLegacyColor?: boolean; // If true, use the old fixed pink color for all environments
}

/**
 * Get the background color for the environment banner based on the environment type.
 * According to KOZA-292:
 * - DEV/UAT/Staging: Green
 * - DEMO/DEMO2: Blue
 * - Preprod: Yellow
 * - Prod: Purple (with warning icon)
 */
function getEnvironmentColor(env: Props["env"]): string {
  switch (env) {
    case "dev":
    case "uat":
    case "staging":
      return colorsLight.success.main; // Green
    case "demo":
    case "demo2":
      return colorsLight.primary.main; // Blue
    case "preprod":
      return colorsLight.warning.main; // Yellow
    case "prod":
      return colorsLight.info.main; // Purple (Hex#6948A6)
    default:
      return colorsLight.secondary.main; // Default (pink) for local, unknown, etc.
  }
}

const useStyles = makeStyles((theme) => ({
  indicator: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: theme.spacing(1),
    width: "100%",
  },
  text: {
    color: colorsLight.white.highEmphasis,
    textAlign: "center",
    padding: theme.spacing(1),
  },
  warningIcon: {
    color: colorsLight.white.highEmphasis,
    fontSize: "1.2rem",
  },
}));

export default function EnvironmentIndicator({ env, children, useLegacyColor = false }: Props) {
  const classes = useStyles();
  // Old behavior: fixed pink color for all environments
  // New behavior: color-coded by environment
  const backgroundColor = useLegacyColor
    ? colorsLight.secondary.main // Old fixed pink color
    : getEnvironmentColor(env);
  const showWarningIcon = env === "prod" && !useLegacyColor; // Only show warning icon with new behavior

  return (
    <div className={classes.indicator} style={{ backgroundColor }}>
      {showWarningIcon && <WarningIcon className={classes.warningIcon} />}
      <Body2 className={classes.text}>{env.toUpperCase()} ENVIRONMENT</Body2>
      {children}
    </div>
  );
}
